import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Modal from 'ui/Modal';
import Loading from 'ui/Loading';
import { Badge } from 'ui/shadcn';
import { cn } from 'lib/utils';
import { staffClient } from 'clients/staff';

/* ------------------------------------------------------------------ *
 * 타입
 * ------------------------------------------------------------------ */

interface WorkTypeStaff {
    chief: string[];
    assistant: string[];
}

interface StaffByWorkType {
    ELECTRIC: WorkTypeStaff;
    TELECOM: WorkTypeStaff;
    FIRE: WorkTypeStaff;
}

interface TimelineSite {
    id: number;
    name: string;
    type: string;
    category?: string;
    workTypes?: string[];
    constructionStartDate?: string;
    constructionEndDate?: string;
    supervisionStartDate?: string;
    supervisionEndDate?: string;
    staffByWorkType?: StaffByWorkType;
}

interface TimelineModalProps {
    isOpen: boolean;
    onClose: () => void;
}

type StatusTab = 'IN_PROGRESS' | 'CONTRACT_COMPLETED' | 'COMPLETED';

/* ------------------------------------------------------------------ *
 * 상수 (DESIGN.md 4px 그리드 / 시맨틱 토큰)
 * ------------------------------------------------------------------ */

const SITE_COL_W = 300; // 현장명 — 넓게 (클릭 시 현장 상세 이동)
const WORKTYPE_COL_W = 92; // 공종 Badge
const STAFF_COL_W = 108; // 감리원 — 짧게 (호버로 전체 명단)
const LEFT_W = SITE_COL_W + WORKTYPE_COL_W + STAFF_COL_W;
const ROW_H = 36; // DESIGN.md §7 조밀 모드

const WORK_TYPE_BADGE: Record<
    string,
    { label: string; variant: 'warning' | 'danger' | 'info' }
> = {
    ELECTRIC: { label: '전기', variant: 'warning' },
    FIRE: { label: '소방', variant: 'danger' },
    TELECOM: { label: '통신', variant: 'info' },
    NETWORK: { label: '통신', variant: 'info' },
};

const WORK_TYPE_ROWS = [
    ['ELECTRIC', '전기'],
    ['TELECOM', '통신'],
    ['FIRE', '소방'],
] as const;

// 상태 3종 톤다운 (SiteList 뱃지 톤과 동일한 계열, 막대는 채도 더 낮춤)
//  진행중 → success / 착공예정 → warning / 준공 → navy(조용한 톤)
const BAR_TONE: Record<StatusTab, string> = {
    IN_PROGRESS: 'bg-success/50',
    CONTRACT_COMPLETED: 'bg-warning/45',
    COMPLETED: 'bg-navy-200',
};

const EMPTY_STAFF: StaffByWorkType = {
    ELECTRIC: { chief: [], assistant: [] },
    TELECOM: { chief: [], assistant: [] },
    FIRE: { chief: [], assistant: [] },
};

/* ------------------------------------------------------------------ *
 * 날짜 → 월 인덱스(소수) 변환
 * ------------------------------------------------------------------ */

const parse = (s?: string): Date | null => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
};

const fmt = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate()
    ).padStart(2, '0')}`;

/** 기준연도 1월을 0으로 두고, 일 단위까지 반영한 월 좌표 */
const monthPos = (d: Date, baseYear: number): number => {
    const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return (
        (d.getFullYear() - baseYear) * 12 + d.getMonth() + (d.getDate() - 1) / daysInMonth
    );
};

/** 막대에 쓰는 기간 — 공사기간 우선, 없으면 감리기간으로 보완(점선 표기) */
const barRange = (site: TimelineSite) => {
    const cs = parse(site.constructionStartDate);
    const ce = parse(site.constructionEndDate);
    if (cs || ce) return { start: cs, end: ce, estimated: false };
    const ss = parse(site.supervisionStartDate);
    const se = parse(site.supervisionEndDate);
    return { start: ss, end: se, estimated: !!(ss || se) };
};

/* ------------------------------------------------------------------ *
 * 감리원 셀 — 축약 + 호버 시 공종별 전체 명단
 * ------------------------------------------------------------------ */

const StaffCell: React.FC<{ staffByWorkType: StaffByWorkType }> = ({ staffByWorkType }) => {
    const [show, setShow] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef<HTMLSpanElement>(null);

    const chiefs = [
        ...staffByWorkType.ELECTRIC.chief,
        ...staffByWorkType.TELECOM.chief,
        ...staffByWorkType.FIRE.chief,
    ];
    const assistants = [
        ...staffByWorkType.ELECTRIC.assistant,
        ...staffByWorkType.TELECOM.assistant,
        ...staffByWorkType.FIRE.assistant,
    ];
    const total = chiefs.length + assistants.length;
    const first = chiefs[0] || assistants[0];

    if (!first) {
        return <span className="text-neutral-400">-</span>;
    }

    const handleMouseEnter = () => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setPosition({ top: rect.top - 8, left: rect.left + rect.width / 2 });
        }
        setShow(true);
    };

    const tooltip =
        show && total > 0
            ? ReactDOM.createPortal(
                  <div
                      className="fixed z-[9999] pointer-events-none"
                      style={{
                          top: position.top,
                          left: position.left,
                          transform: 'translate(-50%, -100%)',
                      }}
                  >
                      <div className="bg-popover text-popover-foreground text-xs rounded border border-border p-3 whitespace-nowrap">
                          <table className="min-w-[200px]">
                              <thead>
                                  <tr className="border-b border-border">
                                      <th className="py-1 pr-3 text-left font-semibold text-muted-foreground">
                                          공종
                                      </th>
                                      <th className="py-1 px-2 text-left font-semibold text-muted-foreground">
                                          책임
                                      </th>
                                      <th className="py-1 pl-2 text-left font-semibold text-muted-foreground">
                                          보조
                                      </th>
                                  </tr>
                              </thead>
                              <tbody>
                                  {WORK_TYPE_ROWS.map(([key, label]) => {
                                      const t = staffByWorkType[key];
                                      return (
                                          <tr key={key}>
                                              <td className="pr-3 py-1 font-medium">{label}</td>
                                              <td className="px-2 py-1">
                                                  {t.chief.length > 0 ? t.chief.join(', ') : '-'}
                                              </td>
                                              <td className="pl-2 py-1">
                                                  {t.assistant.length > 0
                                                      ? t.assistant.join(', ')
                                                      : '-'}
                                              </td>
                                          </tr>
                                      );
                                  })}
                              </tbody>
                          </table>
                      </div>
                  </div>,
                  document.body
              )
            : null;

    return (
        <>
            <span
                ref={triggerRef}
                className="inline-flex max-w-full items-center gap-1 whitespace-nowrap cursor-default"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setShow(false)}
            >
                <span className="truncate text-neutral-900">{first}</span>
                {total > 1 && (
                    <span className="shrink-0 rounded-full bg-navy-50 px-1.5 text-xs font-medium tabular-nums text-primary">
                        +{total - 1}
                    </span>
                )}
            </span>
            {tooltip}
        </>
    );
};

/* ------------------------------------------------------------------ *
 * 본체
 * ------------------------------------------------------------------ */

const TimelineModal: React.FC<TimelineModalProps> = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [timelineData, setTimelineData] = useState<TimelineSite[]>([]);
    const [activeTab, setActiveTab] = useState<StatusTab>('IN_PROGRESS');
    const scrollRef = useRef<HTMLDivElement>(null);

    const fetchTimelineData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await staffClient.get('/site/timeline');
            setTimelineData(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Failed to fetch timeline data:', error);
            setTimelineData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            setActiveTab('IN_PROGRESS');
            fetchTimelineData();
        }
    }, [isOpen, fetchTimelineData]);

    /* ---------------- 탭 + 탭별 정렬 ---------------- */

    const counts = useMemo(() => {
        const base: Record<StatusTab, number> = {
            IN_PROGRESS: 0,
            CONTRACT_COMPLETED: 0,
            COMPLETED: 0,
        };
        timelineData.forEach((s) => {
            if (s.type in base) base[s.type as StatusTab] += 1;
        });
        return base;
    }, [timelineData]);

    const tabs: { key: StatusTab; label: string }[] = [
        { key: 'IN_PROGRESS', label: '진행중' },
        { key: 'CONTRACT_COMPLETED', label: '착공예정' },
        { key: 'COMPLETED', label: '준공' },
    ];

    const sortNote: Record<StatusTab, string> = {
        IN_PROGRESS: '준공 임박순 (준공예정일 빠른 현장부터)',
        CONTRACT_COMPLETED: '착공 임박순 (착공일 공란은 가나다순)',
        COMPLETED: '최근 준공순',
    };

    const rows = useMemo(() => {
        const list = timelineData.filter((s) => s.type === activeTab);
        return list.sort((a, b) => {
            if (activeTab === 'IN_PROGRESS') {
                // 준공예정일 오름차순 (공란은 뒤로)
                const av = a.constructionEndDate || a.supervisionEndDate || '￿';
                const bv = b.constructionEndDate || b.supervisionEndDate || '￿';
                if (av === bv) return a.name.localeCompare(b.name);
                return av < bv ? -1 : 1;
            }
            if (activeTab === 'CONTRACT_COMPLETED') {
                // 착공일 오름차순, 공란은 가나다순으로 뒤에
                const as = a.constructionStartDate || '';
                const bs = b.constructionStartDate || '';
                if (!as && !bs) return a.name.localeCompare(b.name);
                if (!as) return 1;
                if (!bs) return -1;
                if (as === bs) return a.name.localeCompare(b.name);
                return as < bs ? -1 : 1;
            }
            // COMPLETED — 종료일 내림차순
            const av = a.constructionEndDate || a.supervisionEndDate || '';
            const bv = b.constructionEndDate || b.supervisionEndDate || '';
            if (av === bv) return a.name.localeCompare(b.name);
            return av > bv ? -1 : 1;
        });
    }, [timelineData, activeTab]);

    /* ---------------- 연도 범위 자동 계산 (탭 기준) ---------------- */

    const { years, cellW, labelEvery } = useMemo(() => {
        const thisYear = new Date().getFullYear();
        let min = thisYear;
        let max = thisYear;
        rows.forEach((site) => {
            const { start, end } = barRange(site);
            [start, end].forEach((d) => {
                if (!d) return;
                const y = d.getFullYear();
                if (y < 1970 || y > 2100) return; // 이상치 방어
                if (y < min) min = y;
                if (y > max) max = y;
            });
        });
        const list: number[] = [];
        for (let y = min; y <= max; y += 1) list.push(y);
        const span = list.length;
        // 월 칸 폭: 연도 범위가 넓어질수록 좁혀서 가로 스크롤이 과하게 늘어나지 않게
        const w = span <= 5 ? 28 : span <= 10 ? 20 : span <= 20 ? 14 : 10;
        return { years: list, cellW: w, labelEvery: w >= 20 ? 1 : w >= 14 ? 3 : 6 };
    }, [rows]);

    const baseYear = years[0];
    const trackW = years.length * 12 * cellW;

    // 월/연 그리드선 — 칸 폭이 가변이라 Tailwind 클래스로 표현 불가(동적 pitch)
    const gridStyle: React.CSSProperties = {
        width: trackW,
        backgroundImage: [
            `repeating-linear-gradient(to right, hsl(var(--border)) 0 1px, transparent 1px ${
                cellW * 12
            }px)`,
            `repeating-linear-gradient(to right, hsl(var(--border) / 0.5) 0 1px, transparent 1px ${cellW}px)`,
        ].join(','),
    };

    const todayPos = useMemo(() => {
        const now = new Date();
        const p = monthPos(now, baseYear);
        const maxPos = years.length * 12;
        return p >= 0 && p <= maxPos ? p * cellW : null;
    }, [baseYear, years.length, cellW]);

    /* ---------------- 열릴 때/탭 전환 시 오늘 근처로 스크롤 ---------------- */

    useEffect(() => {
        const el = scrollRef.current;
        if (!el || loading || todayPos === null) return;
        el.scrollLeft = Math.max(0, todayPos - (el.clientWidth - LEFT_W) / 3);
    }, [loading, activeTab, todayPos]);

    const handleSiteClick = (siteId: number) => {
        onClose();
        navigate(`/sites/${siteId}`);
    };

    /* ---------------- 렌더 ---------------- */

    const headerCell = (label: string, width: number, extra?: string) => (
        <div
            className={cn(
                'flex items-center px-3 text-sm font-semibold text-neutral-700',
                extra
            )}
            style={{ width }}
        >
            {label}
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="공사기간 현황"
            size="large"
            contentStyle={{ width: '95vw', maxWidth: '95vw', maxHeight: '92vh' }}
        >
            {/* 상단 3탭 — SiteList와 동일한 모양 */}
            <div className="flex items-center gap-1 border-b border-border">
                {tabs.map((tab) => {
                    const active = activeTab === tab.key;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            onClick={() => setActiveTab(tab.key)}
                            className={cn(
                                'relative -mb-px inline-flex items-center gap-2 px-4 h-10 text-md font-medium transition-colors duration-fast border-b-2',
                                active
                                    ? 'border-primary text-foreground'
                                    : 'border-transparent text-neutral-500 hover:text-foreground'
                            )}
                        >
                            <span>{tab.label}</span>
                            <span
                                className={cn(
                                    'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold tabular-nums',
                                    active
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-neutral-600'
                                )}
                            >
                                {counts[tab.key]}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="flex items-center justify-between py-2 text-xs text-muted-foreground">
                <span>정렬: {sortNote[activeTab]}</span>
                <span className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5">
                        <span
                            className={cn('inline-block w-6 h-2 rounded-sm', BAR_TONE[activeTab])}
                        />
                        공사기간 (착공~준공)
                    </span>
                    {rows.some((r) => barRange(r).estimated) && (
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block w-6 h-2 rounded-sm border border-dashed border-neutral-400" />
                            감리기간 (공사기간 미등록)
                        </span>
                    )}
                    {years.length > 0 && (
                        <span className="tabular-nums">
                            {years[0]}~{years[years.length - 1]}년
                        </span>
                    )}
                </span>
            </div>

            {loading ? (
                <div className="py-12">
                    <Loading text="데이터를 불러오는 중..." />
                </div>
            ) : (
                <div
                    ref={scrollRef}
                    className="overflow-auto max-h-[68vh] border border-border rounded"
                >
                    <div style={{ width: LEFT_W + trackW }}>
                        {/* ---------- 헤더 ---------- */}
                        <div className="sticky top-0 z-30 flex bg-neutral-50 border-b border-border">
                            <div
                                className="sticky left-0 z-10 flex shrink-0 bg-neutral-50 border-r border-neutral-300"
                                style={{ width: LEFT_W }}
                            >
                                {headerCell('현장명', SITE_COL_W)}
                                {headerCell('공종', WORKTYPE_COL_W, 'border-l border-border')}
                                {headerCell('감리원', STAFF_COL_W, 'border-l border-border')}
                            </div>
                            <div className="shrink-0" style={{ width: trackW }}>
                                <div className="flex">
                                    {years.map((year) => (
                                        <div
                                            key={year}
                                            className="border-l border-border py-1 text-center text-sm font-semibold text-neutral-700 tabular-nums"
                                            style={{ width: cellW * 12 }}
                                        >
                                            {year}년
                                        </div>
                                    ))}
                                </div>
                                <div className="flex border-t border-border">
                                    {years.map((year) =>
                                        Array.from({ length: 12 }, (_, i) => i + 1).map(
                                            (month) => (
                                                <div
                                                    key={`${year}-${month}`}
                                                    className={cn(
                                                        'py-0.5 text-center text-xs text-neutral-500 tabular-nums',
                                                        month === 1 && 'border-l border-border'
                                                    )}
                                                    style={{ width: cellW }}
                                                >
                                                    {(month - 1) % labelEvery === 0 ? month : ''}
                                                </div>
                                            )
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ---------- 행 ---------- */}
                        {rows.length === 0 ? (
                            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
                                표시할 현장이 없습니다.
                            </div>
                        ) : (
                            rows.map((site) => {
                                const { start, end, estimated } = barRange(site);
                                const staff = site.staffByWorkType || EMPTY_STAFF;
                                const workTypes = site.workTypes || [];

                                let bar: React.CSSProperties | null = null;
                                let roundedL = true;
                                let roundedR = true;
                                if (start || end) {
                                    const maxPos = years.length * 12;
                                    const rawS = start ? monthPos(start, baseYear) : 0;
                                    const rawE = end ? monthPos(end, baseYear) : maxPos;
                                    const s = Math.max(0, Math.min(rawS, maxPos));
                                    const e = Math.max(0, Math.min(rawE, maxPos));
                                    roundedL = rawS >= 0 && !!start;
                                    roundedR = rawE <= maxPos && !!end;
                                    const widthMonths = Math.max(e - s, 0.4);
                                    bar = {
                                        left: s * cellW,
                                        width: widthMonths * cellW,
                                    };
                                }

                                const periodText =
                                    start || end
                                        ? `${estimated ? '감리기간(공사기간 미등록) ' : ''}${
                                              start ? fmt(start) : '착공일 미정'
                                          } ~ ${end ? fmt(end) : '준공일 미정'}`
                                        : '기간 미등록';

                                return (
                                    <div
                                        key={site.id}
                                        className="group flex border-b border-border last:border-b-0"
                                        style={{ height: ROW_H }}
                                    >
                                        <div
                                            className="sticky left-0 z-10 flex shrink-0 bg-background border-r border-neutral-300 group-hover:bg-navy-50"
                                            style={{ width: LEFT_W }}
                                        >
                                            <div
                                                className="flex items-center px-3 min-w-0"
                                                style={{ width: SITE_COL_W }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => handleSiteClick(site.id)}
                                                    title={site.name}
                                                    className="truncate text-left text-base font-medium text-foreground hover:text-info hover:underline"
                                                >
                                                    {site.name}
                                                </button>
                                            </div>
                                            <div
                                                className="flex items-center justify-center gap-1 px-1.5 border-l border-border overflow-hidden"
                                                style={{ width: WORKTYPE_COL_W }}
                                            >
                                                {workTypes.length === 0 ? (
                                                    <span className="text-neutral-400">-</span>
                                                ) : (
                                                    workTypes.map((wt) => {
                                                        const b = WORK_TYPE_BADGE[wt];
                                                        if (!b) return null;
                                                        return (
                                                            <Badge
                                                                key={wt}
                                                                variant={b.variant}
                                                                className="px-1.5 py-0 text-[10px]"
                                                            >
                                                                {b.label}
                                                            </Badge>
                                                        );
                                                    })
                                                )}
                                            </div>
                                            <div
                                                className="flex items-center px-2 text-base border-l border-border overflow-hidden"
                                                style={{ width: STAFF_COL_W }}
                                            >
                                                <StaffCell staffByWorkType={staff} />
                                            </div>
                                        </div>

                                        <div
                                            className="relative shrink-0 group-hover:bg-navy-50/40"
                                            style={gridStyle}
                                        >
                                            {todayPos !== null && (
                                                <div
                                                    className="absolute top-0 bottom-0 w-px bg-navy-300/50"
                                                    style={{ left: todayPos }}
                                                />
                                            )}
                                            {bar && (
                                                <div
                                                    className={cn(
                                                        'absolute top-1/2 -translate-y-1/2 h-[14px]',
                                                        BAR_TONE[activeTab],
                                                        estimated &&
                                                            'opacity-50 border border-dashed border-neutral-400',
                                                        roundedL && 'rounded-l-sm',
                                                        roundedR && 'rounded-r-sm'
                                                    )}
                                                    style={bar}
                                                    title={`${site.name} · ${periodText}`}
                                                />
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default TimelineModal;
